<template>
  <div
    class="screen background"
    :style="{
      backgroundImage: `url('${backgroundImage}')`
    }"
  >
    <div class="screen-container">
      <vv-speech-bubble :title="$t('quiz.yourResults')">
        <div class="speech-bubble-content">
          <vv-progress-circle :color="circleColor" :percentage="progressPercentage" />

          <p v-if="quizResult < 25" class="boost red">{{ $t('quiz.boost0to25') }}</p>
          <p v-if="quizResult >= 25 && quizResult < 50" class="boost orange">
            {{ $t('quiz.boost25to50') }}
          </p>
          <p v-if="quizResult >= 50" class="boost emerald">{{ $t('quiz.boost50to100') }}</p>

          <router-link
            class="check-wrong-response"
            v-if="progressPercentage != 100"
            :to="{ name: 'quizWrongResponses', params: { quizId: quiz.id } }"
          >
            <p>{{ $t('quiz.checkWrongResponse') }}</p>
          </router-link>

          <p>{{ $t('quiz.share') }}</p>

          <div class="social-network">
            <!-- <a target="_blank" href="https://twitter.com/intent/tweet?button_hashtag=VinoVibes&ref_src=twsrc%5Etfw" class="twitter-hashtag-button" :data-text="`I&#39;ve got ${this.quizResult}% score on my quiz, thanks to Vinovibes (https://vinovibes.fr)`" data-url="https://app.vinovibes.fr" data-lang="fr" data-show-count="false">
              <twitter />
            </a> -->
            <a href="https://www.facebook.com/vinovibes.fr" target="_blank"><facebook /></a>
            <a href="https://www.instagram.com/vinovibes.fr/" target="_blank"><instagram /></a>
          </div>
        </div>
      </vv-speech-bubble>
      <div class="cta">
        <vv-button @click="backToProgression" v-if="isComingFromProgression">{{ $t('quiz.backToProgression') }}</vv-button>
        <vv-button @click="backToQuiz" v-else-if="isFromQuiz">Revenir aux quiz</vv-button>
        <vv-button @click="nextLesson" v-else-if="nextLessonStep && !isComingFromProgression">{{ $t('lesson.next') }}</vv-button>
        <vv-button @click="backToTheme" v-else-if="!nextLessonStep && !isComingFromProgression">Revenir aux étapes</vv-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import has from 'lodash/has'

import { RESET_QUIZ_FIREBASE_ID, RESET_RESPONSES } from '@/store/modules/quiz/types'
import { VvButton, VvProgressCircle, VvSpeechBubble } from '@/components/common'
import VvBackButton from '@/components/common/VvBackButton'
import Facebook from '@/components/layout/svg/Facebook'
import Instagram from '@/components/layout/svg/Instagram'
import Twitter from '@/components/layout/svg/Twitter'
import userMixin from '@/mixins/user'
import badgeMixin from '@/mixins/badge'
import { goToNextLessonScreen } from '@/helpers/navigation'

export default {
  name: 'QuizResult',
  components: {
    Facebook,
    Instagram,
    Twitter,
    VvButton,
    VvProgressCircle,
    VvSpeechBubble,
    VvBackButton
  },
  mixins: [userMixin, badgeMixin],
  data() {
    return {
      progressPercentage: 0
    }
  },
  computed: {
    ...mapGetters({ quiz: 'currentQuiz', quizResult: 'quizResult', firebaseId: 'quizFirebaseId', isComingFromProgression: 'getComingFrom' }),
    backgroundImage() {
      if (has(this.quiz, 'resultBackground.file.url')) {
        return this.quiz.resultBackground.file.url
      }

      return null
    },
    circleColor() {
      if (this.quizResult < 25) {
        return 'red'
      }

      if (this.quizResult < 50) {
        return 'orange'
      }

      return '#51da7e' // emerald
    },
    contentType() {
      return this.$route.params.contentType
    },
    currentLessonStep() {
      return Object.values(this.$store.state.lesson.lessonStep.byId).find(
        step => step.quizId === this.quiz.id
      )
    },
    currentMasterclass() {
      if (!this.currentLessonStep) return null

      const masterclass = Object.values(this.$store.getters.getThemes).find(theme =>
        theme.lessonSteps.includes(this.currentLessonStep.id)
      )

      if (!masterclass) return null

      const _masterclass = cloneDeep(masterclass)

      _masterclass.lessonSteps = masterclass.lessonSteps.map(
        id => this.$store.state.lesson.lessonStep.byId[id]
      )

      return _masterclass
    },
    nextLessonStep() {
      if (!this.currentLessonStep || !this.currentMasterclass) return null

      const currentIndex = this.currentMasterclass.lessonSteps.findIndex(
        lessonStep => lessonStep.id === this.currentLessonStep.id
      )

      if (currentIndex >= this.currentMasterclass.lessonSteps.length - 1) return null

      return this.currentMasterclass.lessonSteps[currentIndex + 1]
    },
    isFromQuiz() {
      return this.$route.params.contentType == 'quiz' ? true : false
    }
  },
  mounted() {

    setTimeout(() => {
      this.progressPercentage = this.quizResult
    }, 200)

    if (!this.shouldUnlockQuizBadge(this.quizResult)) return

    switch (this.contentType) {
      case 'masterclass':
        this.unlockMasterclassCompletionBadge(this.quiz.id)
        break

      case 'lessonStep':
      default:
        this.unlockQuizCompletionBadge(this.quiz.id)
        break
    }
  },
  methods: {
    finish() {
      this.$store.commit(RESET_QUIZ_FIREBASE_ID)

      this.$router.push({ name: 'home' })
    },
    backToProgression() {
      this.$store.commit(RESET_QUIZ_FIREBASE_ID)

      let themeId = ''
      
      if (this.currentMasterclass == null) {
        themeId = localStorage.getItem('masterclassId')
      } else {
        themeId = this.currentMasterclass.id
      }
      localStorage.removeItem('masterclassId')
      this.$router.push({ name: 'userProgressionDetail', params: { themeId } })
    },
    nextLesson() {
      this.$store.commit(RESET_QUIZ_FIREBASE_ID)

      goToNextLessonScreen(null, this.currentLessonStep.id, this.currentMasterclass.id, false)
    },
    backToTheme() {
      this.$store.commit(RESET_QUIZ_FIREBASE_ID)
      
      localStorage.setItem('triggerModal', true)
      this.$router.push({ name: 'themeList' })
    },
    backToQuiz() {
      this.$store.commit(RESET_QUIZ_FIREBASE_ID)

      this.$router.push({ name: 'quizList' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';

.speech-bubble-content {
  text-align: center;
  padding-top: 90px;
}

.boost {
  font-size: 16px;
  font-weight: 900;
}

.red {
  color: red;
}

.orange {
  color: orange;
}

.emerald {
  color: $secondary;
}

.check-wrong-response {
  font-size: 16px;
  font-weight: 900;
  color: black;
  text-decoration-color: black;
}

.background {
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
}

.social-network svg {
  margin: 5px;
  width: 30px;
  height: 30px;
}
</style>
